import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Combobox, type ComboboxOption } from '../ui/Combobox';
import { getLocationService } from '../../services/locationServiceInstance';
import type { State, StateWithCountry, Country } from '../../types/location';

export interface StateSelectorProps {
  readonly country: Pick<Country, 'id' | 'iso2'> | null;
  readonly value?: State | null;
  readonly onChange: (state: State | null) => void;
  readonly onCountryResolved?: (countryId: string, countryCode: string, countryName: string) => void;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly label?: string;
  readonly placeholder?: string;
  readonly error?: string;
  readonly className?: string;
}

const MIN_SEARCH = 3;
const DEBOUNCE = 300;

export function StateSelector({
  country,
  value,
  onChange,
  onCountryResolved,
  disabled = false,
  required = false,
  label = 'Estado/Region',
  placeholder = 'Seleccione un estado',
  error,
  className = '',
}: StateSelectorProps) {
  const [statesByCountry, setStatesByCountry] = useState<State[]>([]);
  const [searchResults, setSearchResults] = useState<StateWithCountry[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState<string | null>(null);
  const prevRef = useRef<string | undefined>(country?.id);
  const [autoResolved, setAutoResolved] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mode = country && !autoResolved ? 'byCountry' : 'global';

  useEffect(() => {
    const p = prevRef.current;
    prevRef.current = country?.id;
    if (p !== undefined && p !== country?.id && !autoResolved && value) {
      onChange(null);
    }
  }, [country?.id]);

  useEffect(() => {
    if (autoResolved) return;
    if (country) {
      loadByCountry(country.iso2);
    } else {
      setStatesByCountry([]);
      setSearchResults([]);
    }
  }, [country?.iso2, autoResolved]);

  const loadByCountry = async (iso2: string) => {
    setLoading(true);
    setErrorState(null);
    try {
      const data = await getLocationService().getStates(iso2);
      setStatesByCountry(data);
    } catch (e) {
      setErrorState(e instanceof Error ? e.message : 'Error al cargar estados');
    } finally {
      setLoading(false);
    }
  };

  const searchStates = useCallback(async (q: string) => {
    if (q.length < MIN_SEARCH) {
      setSearchResults([]);
      return;
    }
    setLoading(true);
    setErrorState(null);
    try {
      const data = await getLocationService().getAllStates(q);
      setSearchResults(data);
    } catch (e) {
      setErrorState(e instanceof Error ? e.message : 'Error al buscar estados');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    if (mode !== 'global') return;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (term.length < MIN_SEARCH) {
      setSearchResults([]);
      return;
    }
    timerRef.current = setTimeout(() => searchStates(term), DEBOUNCE);
  }, [mode, searchStates]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const options: ComboboxOption[] = useMemo(() => {
    if (mode === 'byCountry') {
      return [...statesByCountry]
        .sort((a, b) => a.name.localeCompare(b.name, 'es'))
        .map((s) => ({ value: s.id, label: s.name, subtitle: s.iso2 }));
    }
    return searchResults.map((s) => ({
      value: s.id,
      label: s.name,
      subtitle: `${s.iso2} · ${s.countryName || s.countryCode}`,
    }));
  }, [mode, statesByCountry, searchResults]);

  const handleChange = (selectedId: string) => {
    if (!selectedId) {
      onChange(null);
      setAutoResolved(false);
      return;
    }
    if (mode === 'byCountry') {
      const s = statesByCountry.find((x) => x.id === selectedId);
      if (s) onChange(s);
      return;
    }
    const sg = searchResults.find((x) => x.id === selectedId);
    if (!sg) return;
    if (onCountryResolved && sg.countryCode) {
      setAutoResolved(true);
      onCountryResolved(sg.countryId, sg.countryCode, sg.countryName || sg.countryCode);
    }
    onChange({ id: sg.id, name: sg.name, iso2: sg.iso2 });
  };

  // Resolve the Combobox value: prefer id, fallback to iso2 lookup (e.g. hidratando desde datos guardados sin id)
  const resolvedValue = useMemo(() => {
    if (!value) return '';
    if (value.id) return value.id;
    const source = mode === 'byCountry' ? statesByCountry : searchResults;
    const match = source.find((s) => s.iso2 === value.iso2);
    return match?.id ?? '';
  }, [value, mode, statesByCountry, searchResults]);

  const isDisabled = disabled || (mode === 'byCountry' && loading);
  const displayError = error || errorState;

  function getPlaceholder(): string {
    if (mode === 'byCountry' && loading) return 'Cargando estados...';
    if (mode === 'byCountry' && statesByCountry.length === 0 && !loading) return 'No hay estados disponibles';
    if (mode === 'global') return 'Escriba para buscar estado...';
    return placeholder;
  }

  return (
    <div className={className}>
      <Combobox
        options={options}
        value={resolvedValue}
        onChange={handleChange}
        onSearchChange={mode === 'global' ? handleSearchChange : undefined}
        label={label}
        placeholder={getPlaceholder()}
        required={required}
        disabled={isDisabled}
        error={displayError || undefined}
        emptyMessage={loading ? 'Buscando...' : 'No se encontraron estados'}
        allowClear={true}
        minSearchLength={mode === 'global' ? MIN_SEARCH : 0}
      />
    </div>
  );
}